/* eslint-disable no-param-reassign */
import Flow, {
  isChoiceSpec,
  isLevelComponentSpec,
  isParallelSpec,
  FlowSpec
} from '@digibee/flow';
import omit from 'lodash/omit';
import moment from 'moment';
import { toast } from 'react-toastify';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { updateStoresFromFlowSpec } from './helpers';
import { normalizeTriggerSpecFromApi } from './helpers/normalizeTriggerSpecFromApi';

import * as api from '~/api';
import i18n from '~/common/helpers/i18n';
import WebAnalytics from '~/common/helpers/webAnalytics';
import { Capsule } from '~/entities/Capsule';
import { MockType } from '~/entities/Mock';
import { Pipeline } from '~/entities/Pipeline';
import { TriggerSpec } from '~/entities/TriggerSpec';
import { mergeDisconnectedFlowsWithFlowSpec } from '~/scenes/Build/helpers';
import { TestGroup } from '~/scenes/Build/scenes/FlowBuilder/components/Design/machines/TestCase.machine';
import { useCanvasReportContextStore } from '~/scenes/Build/scenes/FlowBuilder/errorReporting/canvasReportContext';
import {
  setCanUpdateInstance,
  fetchInstances
} from '~/scenes/Build/scenes/FlowBuilder/stores/replicasStore';

type ComponentData = Record<string, unknown>;
export type ComponentsData = Record<string, ComponentData>;

type UpdateComponentDataParams = {
  path: (string | number)[];
  value: Record<string, unknown>;
  trackRemap?: [string, string];
};

type SensitiveFields = {
  logSensitiveFields: string[];
};

export type PipelineData = (Pipeline | Capsule) & {
  flowSpec?: FlowSpec | null;
  triggerSpec?: TriggerSpec | null;
  mocks?: MockType[];
  restApiTriggerRoutes?: unknown;
  mcpServerTriggerTools?: unknown;
  isUsingMcpServerTrigger?: boolean;
  stepsDocumentations?: Record<string, string>;
  createdAt?: number;
  projectId?: string | null;
  testCases?: TestGroup[];
  [key: string]: unknown;
};

type SideEffects = {
  navigate?: (path: string) => void;
};

type FetchPipelineParams = {
  id: string;
  realm: string;
};

export type PipelineSettingsParams = {
  description?: string;
  sensitiveFields?: string[];
  inSpec?: Record<string, unknown>;
  outSpec?: Record<string, unknown>;
  parameterizedReplica?: string | null;
  hasInstance?: boolean;
};

export type SavePipelineParams = {
  name: string;
  description?: string;
  preview?: string;
  projectId: string;
  folderId?: string;
  isUpdateVersion?: boolean;
  flowSpec: FlowSpec;
  componentsCount?: number;
  usedComponents?: string[];
  suggestedComponentsIds?: string[];
  connectedOnFlowComponentsCount?: number;
  disconnectedFlowSpecs?: unknown[];
  stepsDocumentations?: Record<string, string>;
  triggerSpec: TriggerSpec & { __documentation__?: string };
  triggerCategory?: string;
  onSuccess?: () => void;
  onFailure?: () => void;
  restApiTriggerRoutes?: unknown;
  isUsingApiTrigger?: boolean;
  mcpServerTriggerTools?: unknown;
  isUsingMcpServerTrigger?: boolean;
  mocks?: MockType[];
  testCases?: TestGroup[];
  useV3Api?: boolean;
};

type PipelineState = {
  pipeline: PipelineData | null;
  fetching: boolean;
  fetched: boolean;
  saving: boolean;
  sensitiveFields: SensitiveFields | null;

  componentsData: ComponentsData | null;
};

type PipelineActions = {
  setFetching: (fetching: boolean) => void;
  setFetched: (fetched: boolean) => void;
  setPipeline: (pipeline: PipelineData | null) => void;
  setSensitiveFields: (sensitiveFields: SensitiveFields | null) => void;
  setProjectId: (projectId: string | null) => void;
  setPipelineSettings: (settings: PipelineSettingsParams) => void;
  setPipelineMocks: (mocks: MockType[]) => void;
  deleteConnectorMocks: (connectorId: string) => void;
  setSaving: (saving: boolean) => void;
  replacePipeline: (pipeline: PipelineData) => void;
  updatePipeline: (pipeline: PipelineData) => void;

  fetchPipeline: (
    params: FetchPipelineParams,
    sideEffects?: SideEffects
  ) => Promise<void>;
  reload: (realm: string) => Promise<void>;
  updatePipelineSettings: (
    data: PipelineSettingsParams,
    realm: string
  ) => Promise<void>;
  save: (
    params: SavePipelineParams,
    context: { realm: string; token: string }
  ) => Promise<boolean | void>;

  setComponentsData: (componentsData: ComponentsData | null) => void;

  reset: () => void;
};

export type { UpdateComponentDataParams };

type PipelineStore = PipelineState & PipelineActions;

const convertUUIDS = (flowSpec: FlowSpec): FlowSpec =>
  Object.keys(flowSpec).reduce((acc, key) => {
    const track = flowSpec[key];
    if (!Array.isArray(track)) return { ...acc, [key]: track };
    return {
      ...acc,
      [key]: [
        ...track.map(step => ({
          ...step,
          id:
            step.id || (step as { uuid?: string }).uuid || crypto.randomUUID(),
          uuid: undefined
        }))
      ]
    };
  }, {} as FlowSpec);

const trackingId = crypto.randomUUID();

const initialState: PipelineState = {
  pipeline: null,
  fetching: true,
  fetched: false,
  saving: false,
  sensitiveFields: null,
  componentsData: null
};

export const usePipelineStore = create<PipelineStore>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      setFetching: fetching => set({ fetching }),

      setFetched: fetched => set({ fetched, fetching: false }),

      setPipeline: pipeline => set({ pipeline }),

      setSensitiveFields: sensitiveFields => set({ sensitiveFields }),

      setProjectId: projectId =>
        set(state => {
          if (state.pipeline) {
            state.pipeline.projectId = projectId;
          }
        }),

      setPipelineSettings: ({
        description,
        sensitiveFields,
        inSpec,
        outSpec,
        parameterizedReplica,
        hasInstance
      }) =>
        set(state => {
          if (state.pipeline) {
            state.pipeline.description =
              description ?? state.pipeline.description;
            state.pipeline.parameterizedReplica = hasInstance
              ? parameterizedReplica
              : null;
            if (state.sensitiveFields) {
              state.sensitiveFields.logSensitiveFields = sensitiveFields || [];
            } else {
              state.sensitiveFields = {
                logSensitiveFields: sensitiveFields || []
              };
            }
            state.pipeline.inSpec = inSpec || {};
            state.pipeline.outSpec = outSpec || {};
          }
        }),

      setPipelineMocks: mocks =>
        set(state => {
          if (!state.pipeline) {
            state.pipeline = { mocks: [] } as unknown as PipelineData;
          }
          state.pipeline.mocks = mocks || [];
        }),

      deleteConnectorMocks: connectorId =>
        set(state => {
          if (state.pipeline?.mocks) {
            state.pipeline.mocks = state.pipeline.mocks.filter(
              mock => mock.connectorId !== connectorId
            );
          }
        }),

      setSaving: saving => set({ saving }),

      replacePipeline: pipeline =>
        set(state => {
          const currentMocks = state.pipeline?.mocks || [];
          const pipelineMocks = pipeline.mocks || [];
          state.pipeline = {
            ...pipeline,
            mocks: pipelineMocks.length > 0 ? pipelineMocks : currentMocks
          };
        }),

      updatePipeline: pipeline => {
        setCanUpdateInstance(!pipeline.parameterizedReplica);
        set(state => {
          const currentMocks = state.pipeline?.mocks || [];
          const pipelineMocks = pipeline.mocks || [];
          state.pipeline = {
            ...pipeline,
            mocks: pipelineMocks.length > 0 ? pipelineMocks : currentMocks
          };
        });
      },

      fetchPipeline: async ({ id, realm }, sideEffects = {}) => {
        const { navigate } = sideEffects;
        const { setFetching, setFetched, setProjectId, reset } = get();

        try {
          performance.mark('pipeline-fetch-start');

          setFetching(true);

          const { data } = await api.pipelineV2.getPipeline({ id, realm });

          if (data.pipeline.parameterizedReplica) {
            await fetchInstances(data.pipeline.parameterizedReplica, realm);
          }

          performance.mark('pipeline-fetch-finish');
          const flowSpec = convertUUIDS(data.pipeline.flowSpec || {});
          const wasModifiedAfterFixLevelComponentsImplementation = moment(
            data.pipeline.lastModified
          ).isAfter(moment('2023-07-01'));
          const fullFlowSpec = mergeDisconnectedFlowsWithFlowSpec(
            flowSpec || {},
            data.pipeline.disconnectedFlowSpecs || [],
            wasModifiedAfterFixLevelComponentsImplementation
          );
          const flow = new Flow(fullFlowSpec);
          const stepsDocumentations = data.pipeline.stepsDocumentations || {};

          const documentationFieldMapper = (currentFlow: Flow) => {
            if (Object.keys(stepsDocumentations).length === 0)
              return currentFlow;
            const f = currentFlow.mapComponents(componentSpec => {
              if (isChoiceSpec(componentSpec)) {
                return {
                  ...componentSpec,
                  __documentation__: stepsDocumentations[componentSpec.id],
                  when: componentSpec.when.map((config, idx) => ({
                    ...config,
                    __documentation__:
                      stepsDocumentations[`${componentSpec.id}.when.${idx}`]
                  })),
                  __otherwiseDocumentation__:
                    stepsDocumentations[`${componentSpec.id}.otherwise`]
                };
              }

              if (isParallelSpec(componentSpec)) {
                return {
                  ...componentSpec,
                  __documentation__: stepsDocumentations[componentSpec.id],
                  params: {
                    ...componentSpec.params,
                    parallelExecutions:
                      componentSpec.params.parallelExecutions.map(
                        (config, idx) => ({
                          ...config,
                          __documentation__:
                            stepsDocumentations[
                              `${componentSpec.id}.executions.${idx}`
                            ]
                        })
                      )
                  }
                };
              }

              return {
                ...componentSpec,
                __documentation__: stepsDocumentations[componentSpec.id]
              };
            });
            return f;
          };

          const setParamsOnException = (currentFlow: Flow) => {
            const f = currentFlow.mapComponents(componentSpec => {
              if (
                isLevelComponentSpec(componentSpec) &&
                !componentSpec?.params?.onException
              ) {
                return {
                  ...componentSpec,
                  params: {
                    ...componentSpec.params,
                    onException: `${componentSpec.id}-onExceptionTrack`
                  }
                };
              }

              return componentSpec;
            });
            return f;
          };

          const componentsData = flow
            .map(setParamsOnException)
            .map(documentationFieldMapper);

          const triggerSpec = {
            ...normalizeTriggerSpecFromApi(data.pipeline.triggerSpec),
            __documentation__: stepsDocumentations.trigger,
            name: (() => {
              if (data.pipeline.isUsingApiTrigger) {
                return 'api';
              }
              return data.pipeline.triggerSpec?.name;
            })()
          };

          const existingMocks = get().pipeline?.mocks || [];
          const backendMocks = data.pipeline.mocks || [];

          setCanUpdateInstance(!data.pipeline.parameterizedReplica);
          set({
            sensitiveFields: data.sensitiveFields || null,
            componentsData: componentsData.spec() as unknown as ComponentsData,
            pipeline: {
              ...data.pipeline,
              triggerSpec,
              inSpec: data.pipeline.inSpec || {},
              outSpec: data.pipeline.outSpec || {},
              mocks: backendMocks.length > 0 ? backendMocks : existingMocks
            } as PipelineData
          });

          updateStoresFromFlowSpec(componentsData.spec());

          setFetched(true);

          if (id === 'new') {
            const urlParams = new URLSearchParams(window.location.search);
            const projectId = urlParams.get('projectId');
            setProjectId(projectId);
          }
        } catch {
          reset();
          navigate?.(`/${realm}/operation/build/pipelines`);
        }
      },

      reload: async realm => {
        const { pipeline, fetchPipeline } = get();
        if (pipeline?.id) {
          return fetchPipeline({ id: pipeline.id, realm });
        }
        return undefined;
      },

      updatePipelineSettings: async (data, realm) => {
        const { setPipelineSettings } = get();

        setPipelineSettings(data);

        if (data.parameterizedReplica) {
          await fetchInstances(data.parameterizedReplica, realm);
        }
      },

      save: async (params, context) => {
        const { realm, token } = context;
        const { pipeline, sensitiveFields, setSaving, updatePipeline } = get();

        if (!pipeline) return false;

        const {
          name,
          description,
          preview,
          projectId,
          folderId,
          isUpdateVersion,
          flowSpec,
          componentsCount,
          usedComponents,
          suggestedComponentsIds,
          connectedOnFlowComponentsCount,
          disconnectedFlowSpecs,
          stepsDocumentations,
          triggerSpec,
          triggerCategory,
          onSuccess,
          onFailure,
          restApiTriggerRoutes,
          isUsingApiTrigger,
          mcpServerTriggerTools,
          isUsingMcpServerTrigger,
          mocks,
          testCases,
          useV3Api = false
        } = params;

        const pipelineData = omit(pipeline, [
          'lastModified',
          'disabled',
          'canvasVersion'
        ]);

        setSaving(true);

        try {
          const saveImage = async (image: string) => {
            try {
              return await api.pipelineV2.saveImage({
                token,
                image,
                realm
              });
            } catch {
              return undefined;
            }
          };

          const isCreate = !pipelineData.id;
          const folderIdToSend = isCreate ? folderId : undefined;

          const triggerSpecRecord = triggerSpec as Record<string, unknown>;
          /* Trigger spec uses __documentation__ from API; bracket access avoids no-underscore-dangle on identifiers. */
          // eslint-disable-next-line dot-notation
          const triggerDocForSteps = triggerSpecRecord['__documentation__'];

          const data = {
            realm,
            trackingId,
            projectId,
            folderId: folderIdToSend,
            pipeline: {
              ...omit(pipelineData, ['mocks', 'testCases', 'projectId']),
              name: pipelineData.name === 'Untitled' ? name : pipelineData.name,
              description: description || pipelineData.description,
              previewName: preview ? await saveImage(preview) : undefined,
              flowSpec,
              restApiTriggerRoutes,
              isUsingApiTrigger,
              mcpServerTriggerTools,
              isUsingMcpServerTrigger,
              disconnectedFlowSpecs,
              componentsCount,
              connectedOnFlowComponentsCount,
              usedComponents,
              suggestedComponentsIds,
              stepsDocumentations: {
                ...stepsDocumentations,
                trigger:
                  triggerDocForSteps !== undefined
                    ? triggerDocForSteps
                    : stepsDocumentations?.trigger
              },
              triggerSpec: omit(triggerSpec, [
                '__documentation__',
                'triggerCategory'
              ]),
              triggerCategory
            },
            mocks: mocks || [],
            testCases: testCases || []
          };

          const saveApi = useV3Api ? api.pipelineV3 : api.pipelineV2;

          const updatePipelineResult = await saveApi.save(
            data as unknown as Parameters<typeof saveApi.save>[0]
          );

          const sensitiveFieldsData = {
            realm,
            pipelineId: updatePipelineResult.id,
            sensitiveFields: sensitiveFields?.logSensitiveFields || []
          };

          await api.pipelineV2.updateSensitiveFields(sensitiveFieldsData);

          onSuccess?.();

          const currentMocks = pipeline?.mocks || [];

          updatePipeline({
            ...pipelineData,
            ...updatePipelineResult,
            restApiTriggerRoutes,
            mcpServerTriggerTools,
            mocks: currentMocks
          } as PipelineData);

          if (updatePipelineResult.id) {
            const pipelineBaseURL = `/${realm}/design/v2/pipelines`;

            if (
              window.location.pathname.includes(pipelineBaseURL) &&
              window.location.pathname.includes(updatePipelineResult.id) ===
                false
            ) {
              const newURL = `${pipelineBaseURL}/${updatePipelineResult.id}`;
              window.history.replaceState(null, '', newURL);
            }

            if (isUpdateVersion) window.location.reload();

            toast.success(
              i18n.t(
                isUsingApiTrigger
                  ? 'scenes.pipelines.configure_api.save_success'
                  : 'label.pipeline_saved_msg_success'
              )
            );
            return true;
          }

          return false;
        } catch (error) {
          onFailure?.();
          const err = error as Error;
          if (err.message.includes('409')) {
            WebAnalytics.sendEvent('[Build] (Pipeline Save) Conflict', {
              errorMessage: err.message,
              data: {
                realm,
                trackingId,
                projectId,
                pipeline: {
                  createdAt: pipelineData.createdAt,
                  draft: pipelineData.draft,
                  name:
                    pipelineData.name === 'Untitled' ? name : pipelineData.name,
                  id: pipelineData.id,
                  versionMajor: pipelineData.versionMajor,
                  versionMinor: pipelineData.versionMinor
                }
              }
            });

            toast.error(
              `${i18n.t('label.update_pipeline_msg_error')} (${err.message.replace(
                'GraphQL error:',
                'Error:'
              )})`
            );
            return false;
          }

          toast.error(err.message);
          return false;
        } finally {
          setSaving(false);
        }
      },

      setComponentsData: componentsData => set({ componentsData }),

      reset: () => {
        const { pipeline } = get();
        if (pipeline) {
          // Preserve identity for crash reporting before teardown clears it.
          useCanvasReportContextStore.getState().setPipelineIdentity({
            pipelineName:
              pipeline.name ?? (pipeline.title as string | undefined) ?? null,
            pipelineDescription: pipeline.description ?? null
          });
        }
        set({ ...initialState });
      }
    })),
    { name: 'PipelineStore' }
  )
);

export const reloadPipeline = (realm: string) =>
  usePipelineStore.getState().reload(realm);

export const setPipelineMocks = (mocks: MockType[]) =>
  usePipelineStore.getState().setPipelineMocks(mocks);

export const deleteConnectorMocks = (connectorId: string) => {
  const { pipeline } = usePipelineStore.getState();
  if (!pipeline?.mocks?.some(mock => mock.connectorId === connectorId)) return;
  usePipelineStore.getState().deleteConnectorMocks(connectorId);
};
